import { v4 as uuid } from 'uuid';
import { Translate } from 'next-translate';

import { CustomColumnHeader, getAdderColumnType, ID, LineItem, Lot, LotChanges } from 'types';
import {
  INTERNAL_PART_NUMBER_COLUMN_NAME,
  SUPPLIER_PART_NUMBER_COLUMN_NAME,
} from '@components/bid_request/details/constants';
import { matchesNew } from './matchesNew';
import { mergeLineItems } from './emptyLineItem';
import { createDefaultHeader, mergeCustomColumnHeaders } from './emptyCustomColumnHeader';

const DEFAULT_NUMBER = 1;

export const isNewLot = (lot: Lot | LotChanges): boolean => matchesNew(String(lot.id)) && !lot._destroy;

export const getNewLots = (lotChanges?: Record<ID, LotChanges>): Lot[] => {
  const hasLotChanges = lotChanges && Object.keys(lotChanges)?.length;

  if (!hasLotChanges) return [];
  return Object.values(lotChanges)
    .filter(isNewLot)
    .map((lot: LotChanges): Lot => {
      const lineItems = !lot.line_items?.length ? [] : Object.values(lot.line_items) as unknown as LineItem[];
      const headers = !lot.custom_column_headers?.length ? [] : Object.values(lot.custom_column_headers);
      const newLot: Lot = { ...lot, custom_column_headers: headers, line_items: lineItems };
      newLot.line_items = mergeLineItems(newLot, lotChanges);
      newLot.custom_column_headers = mergeCustomColumnHeaders(newLot, lotChanges);

      return newLot;
    });
};

export const isEmptyStateLot = (lot: Lot | LotChanges): boolean => {
  if (!lot) return true;

  if (lot._destroy) return false;
  const { line_items: lineItems } = lot;

  if (Array.isArray(lineItems)) {
    // Check if it has any non-destroyed items.
    return !lineItems?.filter((lineItem: LineItem): boolean => (
      !lineItem._destroy
    )).length;
  }
  // Check if it has any non-destroyed items.
  return !lineItems || !Object.keys(lineItems)?.filter((key: string): boolean => (
    !lineItems[key]._destroy
  )).length;
};

/** When a lot is persisted and not empty on an event with responses, it cannot be deleted. */
export const persistedWithResponses = ({ lot, hasResponses }: {
  hasResponses: boolean;
  lot: Lot;
}): boolean => {
  if (isEmptyStateLot(lot) || isNewLot(lot)) return false;
  return hasResponses;
};

export const hasEmptyNewLot = (lotChanges?: Record<ID, LotChanges>): boolean => {
  const hasLotChanges = lotChanges && Object.keys(lotChanges)?.length;

  if (!hasLotChanges) return false;
  const newLots = getNewLots(lotChanges);

  return newLots.some(isEmptyStateLot);
};

export const mergeLots = ({ lots, lotChanges }: {
  lotChanges?: Record<ID, LotChanges>;
  lots?: Lot[];
}): Lot[] => {
  const hasLotChanges = lotChanges && Object.keys(lotChanges)?.length;
  const result: Record<ID, Lot> = {};

  if (hasLotChanges) {
    if (lots?.length) {
      const changedLots = lots.map((lot: Lot) => ({
        ...lot,
        ...lotChanges[lot.id],
        custom_column_headers: mergeCustomColumnHeaders(lot, lotChanges),
        line_items: mergeLineItems(lot, lotChanges),
      }));

      // Update existing lots with changes.
      changedLots.forEach((lot: Lot) => {
        result[lot.id] = { ...result[lot.id], ...lot };
      });
    }
    const newLots = getNewLots(lotChanges);

    // Add new lots.
    newLots.forEach((lot: Lot) => {
      result[lot.id] = { ...result[lot.id], ...lot };
    });
  } else if (lots?.length) {
    lots.forEach((lot: Lot) => {
      result[lot.id] = { ...result[lot.id], ...lot };
    });
  }

  return Object.values(result);
};

export const getNextLotSequentialNumber = ({ lots, lotChanges }: {
  lotChanges?: Record<ID, LotChanges>;
  lots?: Lot[];
} = {}): number => {
  // Merge existing lots with changes.
  const mergedLots = mergeLots({ lotChanges, lots });

  if (!mergedLots?.length) return DEFAULT_NUMBER;
  // Filter out deleted lots.
  const validLots = mergedLots.filter(lot => !lot._destroy);

  if (!validLots?.length) return DEFAULT_NUMBER;
  // Get all sequential numbers.
  const numbers: number[] = validLots.map(({ sno }) => sno);

  if (!numbers.length) return DEFAULT_NUMBER;
  // Get the next sequential number.
  return Math.max(...numbers) + 1;
};

const createLotId = (): string => `new-${uuid()}`;

const cloneAdderHeadersFromExistingLots = ({ lotChanges, lots, newLotId }: {
  lotChanges?: Record<ID, LotChanges>;
  lots?: Lot[];
  newLotId: ID;
}): Record<ID, CustomColumnHeader> => {
  const mergedLots = mergeLots({ lotChanges, lots });
  if (!mergedLots?.length) return {};

  const adderType = getAdderColumnType();
  const referenceLot = mergedLots.find(
    lot => !lot._destroy && lot.custom_column_headers?.some(h => h.column_type === adderType && !h._destroy),
  );

  if (!referenceLot) return {};

  const result: Record<ID, CustomColumnHeader> = {};
  // Only clone event-wide adders (lot_ids null/empty). Lot-scoped adders belong
  // to specific lots and must not be propagated to newly created lots.
  referenceLot.custom_column_headers
    .filter(h => h.column_type === adderType && !h._destroy && !h.adder_column_config?.lot_ids?.length)
    .forEach(h => {
      const newHeaderId = `new-${uuid()}`;
      result[newHeaderId] = {
        ...h,
        adder_column_config: h.adder_column_config ? { ...h.adder_column_config } : h.adder_column_config,
        created_at: '',
        id: newHeaderId,
        lot_id: newLotId,
        updated_at: '',
      };
    });

  return result;
};

export const createDefaultLot = ({ id, isPriceUpdate = false, lots, lotChanges, t }: {
  id?: string;
  isPriceUpdate?: boolean;
  lotChanges?: Record<ID, LotChanges>;
  lots?: Lot[];
  t: Translate;
}): LotChanges => {
  const newId = id?.length ? id : createLotId();
  const title = t('general.description');
  const nextSequentialNumber = getNextLotSequentialNumber({ lotChanges, lots });
  const header = createDefaultHeader({ isSupplierVisible: true, lotId: newId, order: 0, title });
  // Price-update events additionally seed two supplier-visible descriptive columns
  // ("Internal Part Number", "Supplier Part Number"), mirroring the server's build_default_lot.
  // Labels are kept verbatim in sync with the server's hardcoded display_text so the columns
  // carry one stable name and the upload remap (getCustomMatchedHeaders) reconciles into them.
  const partNumberHeaders = isPriceUpdate
    ? [INTERNAL_PART_NUMBER_COLUMN_NAME, SUPPLIER_PART_NUMBER_COLUMN_NAME].map((partNumberTitle, index) =>
      createDefaultHeader({ isSupplierVisible: true, lotId: newId, order: index + 1, title: partNumberTitle }))
    : [];
  const adderHeaders = cloneAdderHeadersFromExistingLots({ lotChanges, lots, newLotId: newId });

  return {
    created_at: '',
    custom_column_headers: {
      [header.id]: header,
      ...Object.fromEntries(partNumberHeaders.map(h => [h.id, h])),
      ...adderHeaders,
    },
    description: '',
    has_baseline: false,
    id: newId,
    line_item_questions_count: 0,
    line_items: {},
    line_items_count: 0,
    name: '',
    sno: nextSequentialNumber,
    updated_at: '',
  };
};
